import { Component, HostListener, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { LoaderComponent } from './shared/component/loader/loader.component';
import { TapToTopComponent } from './shared/component/tap-to-top/tap-to-top.component';
import Swal from 'sweetalert2';
import { SwalService } from './shared/services/SwalService.service';
import { ApiCallsService } from './shared/services/api-calls.service';
import { AuthService } from './auth/auth.service';
import { SwUpdate } from '@angular/service-worker';
import { filter } from 'rxjs';
import { NavmenuService } from './shared/services/navmenu.service';

@Component({
  selector: 'app-root',
  imports: [CommonModule, RouterOutlet, LoaderComponent, TapToTopComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})  
export class AppComponent implements OnInit {
  isSwalOpen = false;
  idleTime = 0;
  idleLimit = 300; // 5 minutes
  idleTimerInterval: any;
  sessionTimeoutLogin: any;
  waitingForIdle = false;
  currentRoute: string = '';

  constructor(private readonly router: Router, private readonly swalService: SwalService,private readonly apiservice:AuthService,private readonly swUpdate: SwUpdate,private navServices :NavmenuService ) {}

private normalizeUrl(url: string): string {
  return url.split('?')[0].replace(/\/+$/, '');
}

private routeMatchScore(route: string, url: string): number {
  const routeSegs = this.normalizeUrl(route).split('/').filter(Boolean);
  const urlSegs = this.normalizeUrl(url).split('/').filter(Boolean);
  if (routeSegs.length > urlSegs.length) return -1;
  for (let i = 0; i < routeSegs.length; i++) {
    if (routeSegs[i] !== urlSegs[i]) return -1;
  }
  return routeSegs.length; // longer/more specific match wins
}

private flattenConfigNodes(nodes: any[], acc: { route: string; config: any }[] = []) {
  for (const n of nodes || []) {
    if (n.config) acc.push({ route: n.path?.route || '', config: n.config });
    if (n.children?.length) this.flattenConfigNodes(n.children, acc);
  }
  return acc;
}

private resolveSideComponentConfig(url: string): void {
  const raw = localStorage.getItem('sidebarItems');
  if (!raw) return;

  let tree: any[];
  try {
    tree = JSON.parse(raw);
  } catch {
    return;
  }

  const flat = this.flattenConfigNodes(tree);
  let best: { route: string; config: any } | null = null;
  let bestScore = -1;

  for (const item of flat) {
    const score = this.routeMatchScore(item.route, url);
    if (score > bestScore) {
      bestScore = score;
      best = item;
    }
  }

  if (best) {
    localStorage.setItem('SideComponentConfig', JSON.stringify(best.config));
  }
}
  ngOnInit() {
    this.navServices.noAccessTriggered$.subscribe(() => {
      Swal.fire({
        title: 'Access Denied',
        text: 'You do not have permission to access this page.',
        icon: 'warning',
        confirmButtonText: 'OK',
        confirmButtonColor: '#7366ff'
      }).then(() => {
        const lastActive = localStorage.getItem('lastActiveUrl') || localStorage.getItem('localRedirection') || '/site_summary';
        if (this.router.url !== lastActive) {
          this.router.navigateByUrl(lastActive);
        }
      });
    });

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.currentRoute = event.urlAfterRedirects;
        if (this.currentRoute.startsWith('/auth')) {
          clearTimeout(this.sessionTimeoutLogin);
          clearInterval(this.idleTimerInterval);
        } else {
          if (!this.currentRoute.startsWith('/auth') && this.currentRoute !== '/error-404' && this.currentRoute !== '/no-access') {
            localStorage.setItem('lastActiveUrl', this.currentRoute);
          }
          this.startSessionTimerLogin();
          this.startIdleTimer(); // start tracking idle time
        }
      }
        this.router.events
    .pipe(filter(e => e instanceof NavigationEnd))
    .subscribe((e: NavigationEnd) => this.resolveSideComponentConfig(e.urlAfterRedirects));

  // tree may load asynchronously (API call) — re-resolve for whatever the
  // current url is once it lands, and again any time it's rebuilt (site switch)
  this.navServices.sidebarItemsUpdated$.subscribe(() => {
    this.resolveSideComponentConfig(this.router.url);
  });

  // cover the very first paint, in case NavigationEnd already fired
  // before this subscription was set up
  this.resolveSideComponentConfig(this.router.url);
    });
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('d');
      localStorage.setItem('redirect', token ? 'Y' : 'N');

      if (token && !window.location.pathname.includes('/auth/login')) {
        this.router.navigate(['/auth/login'], {
          queryParams: { d: token },
          replaceUrl: true
        });
      }
  }

  @HostListener('window:mousemove')
  @HostListener('window:keydown')
  @HostListener('window:click')
  resetIdleTime() {
    this.idleTime = 0;
    const expirySeconds = Number(localStorage.getItem('loginSession'));
    if (!isNaN(expirySeconds) && expirySeconds > 0) {
      const freshExpiry = new Date(Date.now() + expirySeconds * 1000);
      localStorage.setItem('calculatedExpiry', freshExpiry.toISOString());
      sessionStorage.setItem('calculatedExpiry', freshExpiry.toISOString());
    }
  }

  startIdleTimer() {
    if (this.idleTimerInterval) clearInterval(this.idleTimerInterval);
    this.idleTimerInterval = setInterval(() => {
      this.idleTime++;
      if (this.waitingForIdle && this.idleTime >= this.idleLimit) {
        this.waitingForIdle = false;
        this.showSessionTimeoutAlert();
      }
    }, 1000); // Check every second
  }

async showSessionTimeoutAlert() {
  const result = await this.swalService.showTimerAlert({
    title: 'Session Timeout!',
    html: 'You will be logged out in <strong>60</strong> seconds.',
    timerSeconds: 60,
    confirmButtonText: 'Logout Now',
    cancelButtonText: 'Stay In'
  });

  if (result.isConfirmed || result.dismiss === Swal.DismissReason.timer) {
    localStorage.removeItem('token');
    localStorage.removeItem('calculatedExpiry');
    sessionStorage.removeItem('calculatedExpiry');
    localStorage.removeItem('loginTimestamp');
    this.router.navigate(['/auth/login']);
  } else if (result.dismiss === Swal.DismissReason.cancel) {
    this.apiservice.resetToken().subscribe(data => {
      const dataToken = data.Data.token;
      const expirySeconds = parseInt(data.Data.expiry_time, 10); // assuming expiry_time is returned in seconds

      // Store the new token
      localStorage.setItem('token', dataToken);

      if (!isNaN(expirySeconds)) {
        // Add expirySeconds to current time
        const now = new Date();
        const newExpiryDate = new Date(now.getTime() + expirySeconds * 1000);
        localStorage.setItem('calculatedExpiry', newExpiryDate.toISOString());
        sessionStorage.setItem('calculatedExpiry', newExpiryDate.toISOString());
      }

      this.startSessionTimerLogin(); // Restart the session timeout
    });
  }
}


  startSessionTimerLogin = () => {
    const expiryTime = Number(localStorage.getItem('loginSession')); // in seconds
    if (this.sessionTimeoutLogin) clearTimeout(this.sessionTimeoutLogin);

    this.sessionTimeoutLogin = setTimeout(() => {
      if (this.idleTime >= this.idleLimit) {
        this.showSessionTimeoutAlert();
      } else {
        this.waitingForIdle = true; // wait until user becomes idle
      }
    }, expiryTime * 1000);
  };
}
